import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { InputStaticFieldsComponent } from '../input-static-fields/input-static-fields.component';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Validation } from '../../common/validation/validation';
import { Subscription } from 'rxjs';
import { CommonHttpsService } from '../../services/https/common-https.service';
import { ToasterMessageService } from '../../services/topbar-toaster/toaster-message.service';
import { apiUrls } from '../../common/api_urls/api_urls';
import { LoaderService } from '../../services/loader/loader.service';
import { AuthService } from '../../services/auth.service';
import { HttpHeaders } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { TranslationService } from '../../services/translation/translation.service';
import { headers } from '../../common/headers';
import { noWhitespaceValidator } from '../../helper/validtors-helper';

@Component({
  selector: 'app-update-password',
  imports: [InputStaticFieldsComponent, ReactiveFormsModule, CommonModule],
  templateUrl: './update-password.component.html',
  styleUrl: './update-password.component.css',
})
export class UpdatePasswordComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();
  public authService = inject(AuthService);

  UpdatePasswordForm!: FormGroup;
  Validation = Validation;
  @Output() modalDismiss = new EventEmitter<void>();
  private translationService = inject(TranslationService);
  showErrorDiv: boolean = false;
  currentMessage!: string;
  // Password strength and validation states
  passwordStrength: string = '';
  passwordStrengthPercentage: number = 0;
  passwordRequirements = {
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  };

  constructor(
    public fb: FormBuilder,
    public commonHttpService: CommonHttpsService,
    public messageService: ToasterMessageService,
    public loadingService: LoaderService,
  ) {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.UpdatePasswordForm = this.fb.group(
      {
        previouspassword: ['', [Validators.required, Validators.maxLength(15), noWhitespaceValidator]],
        currentpassword: ['', [Validators.required, Validators.maxLength(15), noWhitespaceValidator]],
        repeatpassword: ['', [Validators.required, Validators.maxLength(15), noWhitespaceValidator]],
      },
      { validator: this.passwordsMatchValidator },
    );

    this.UpdatePasswordForm.get('currentpassword')?.valueChanges.subscribe(
      (password: string) => {
        this.checkPasswordStrength(password);
      },
    );
  }

  passwordsMatchValidator(
    formGroup: FormGroup,
  ): { [key: string]: boolean } | null {
    const currentPassword = formGroup.get('currentpassword')?.value;
    const repeatPassword = formGroup.get('repeatpassword')?.value;
    if (
      currentPassword &&
      repeatPassword &&
      currentPassword !== repeatPassword
    ) {
      return { passwordsMismatch: true };
    }
    return null;
  }

  checkPasswordStrength(password: string) {
    if (!password) {
      this.passwordStrength = '';
      this.passwordStrengthPercentage = 0;
      this.passwordRequirements = {
        minLength: false,
        uppercase: false,
        lowercase: false,
        number: false,
        specialChar: false,
      };
      return;
    }

    this.passwordRequirements = {
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      // specialChar: /[@$!%*?&]/.test(password)
      specialChar: /[@$!%*?&#^()_+\-=\[\]\{\}\|\\:;"'<>,\.\/`]/.test(password),
    };

    const strengthScore = Object.values(this.passwordRequirements).filter(
      Boolean,
    ).length;
    if (strengthScore <= 2) {
      this.passwordStrength = 'Weak';
      this.passwordStrengthPercentage = 33;
    } else if (strengthScore <= 4) {
      this.passwordStrength = 'Medium';
      this.passwordStrengthPercentage = 66;
    } else {
      this.passwordStrength = 'Strong';
      this.passwordStrengthPercentage = 100;
    }
  }

  translate(key: string): string {
    return this.translationService.translate(key);
  }

  onSubmit(): void {
    if (this.passwordStrength !== 'Strong') {
      // this.messageService.errroMessage('Please Enter Strong Password', true);
      this.showErrorDiv = true;
      this.currentMessage = 'Please Enter Strong Password';
      return;
    }
    if (this.UpdatePasswordForm.valid) {
      const token = this.authService.getAccessToken();
      const userData = this.authService.getUserData();
      const bodyData = {
        username: userData.username,
        newPassword: this.UpdatePasswordForm.value.currentpassword,
        oldPassword: this.UpdatePasswordForm.value.previouspassword,
        accessToken: token,
      };
       ;
      const header = new HttpHeaders({ [headers.lookupHeader]: 'true' });

      this.subscription.add(
        this.commonHttpService
          .AddEntity(apiUrls.authentication.changePassword, bodyData, {
            headers: header,
          })
          .subscribe({
            next: (data) => {
              this.loadingService.stopSpinner();
              // this.messageService.successMessage(
              //   'Password updated successfully',
              //   true,
              // );
              this.showErrorDiv = true;
              this.currentMessage = 'Password updated successfully';
              this.authService.logout();
              this.closeModal();
            },
            error: (err) => {
               ;
              this.loadingService.stopSpinner();
              // Handle different error messages
              let apiError = err.error;

              // Handle JSON string error (e.g., attempt limit exceeded)
              if (apiError.startsWith('{"error":"')) {
                try {
                  const parsedError = JSON.parse(apiError).error;
                  this.currentMessage = parsedError.replace(
                    'Error changing password: ',
                    '',
                  );
                } catch (e) {
                  this.currentMessage = apiError;
                }
              }
              // Handle plain error message (e.g., previously used password)
              else if (apiError.startsWith('Error changing password: ')) {
                this.currentMessage = apiError.replace(
                  'Error changing password: ',
                  '',
                );
              }
              // Fallback for unexpected formats
              // else {
              //   this.currentMessage = apiError;
              // }

              this.showErrorDiv = true;
            },
          }),
      );
    }
  }
  closeModal() {
    this.modalDismiss.emit();
  }
}
